import {
  hasAffiliateMarketingConsent,
  isValidAffiliateReferral,
  STORAGE_CONSENT_EVENT,
  StorageConsentEventDetail,
} from '../privacy/storageConsent';
import {
  markToltRuntimeStarted,
  revokeToltRuntime,
  TOLT_PRIMARY_SCRIPT_URL,
} from '@vectormethods/storage-consent/tolt-runtime';

const TOLT_SCRIPT_ID = 'vectormethods-tolt-tracker';
const TOLT_LOADED_EVENT = 'toltScriptLoaded';
const TOLT_REFERRAL_READY_EVENT = 'tolt_referral_ready';
const TOLT_READY_TIMEOUT_MS = 10_000;
const TOLT_READY_POLL_MS = 50;
const AFFILIATE_BOOTSTRAP_PREFLIGHT_TIMEOUT_MS = 3_000;
const AFFILIATE_HEADER_NAME = 'X-Tolt-Referral';
const OPAQUE_LEAD_IDENTIFIER_PATTERN = /^vm_tolt_[0-9a-f]{32}$/;
const TOLT_PUBLIC_ID_PATTERN = /^pk_[A-Za-z0-9]{1,253}$/;

interface ToltData {
  click_id?: string | null;
  customer_id?: string | null;
  partner_id?: string | null;
  program_id?: string | null;
}

interface ToltSignupResult {
  customer_id?: string | null;
  message?: string;
}

type ToltFunction = ((command: string, data?: unknown) => Promise<unknown>) & {
  signup?: (identifier: string) => Promise<ToltSignupResult>;
};

declare global {
  interface Window {
    tolt?: ToltFunction;
    tolt_data?: ToltData | null;
    tolt_referral?: string | null;
  }
}

let toltReadyPromise: Promise<boolean> | null = null;
let cancelToltReadyWait: (() => void) | null = null;
let affiliateBootstrapPreflightKey: string | null = null;
let affiliateBootstrapPreflightPromise: Promise<void> | null = null;
const completedLeadIdentifiers = new Set<string>();
const inFlightLeadSubmissions = new Map<string, Promise<boolean>>();

function isBrowser(): boolean {
  return typeof window !== 'undefined' && typeof document !== 'undefined';
}

function readCookieValue(name: string): string | null {
  if (!isBrowser()) return null;

  const prefix = `${name}=`;
  for (const segment of document.cookie.split(';')) {
    const candidate = segment.trim();
    if (!candidate.startsWith(prefix)) continue;
    const rawValue = candidate.slice(prefix.length);
    try {
      return decodeURIComponent(rawValue);
    } catch {
      return null;
    }
  }
  return null;
}

function getConfiguredPublicId(): string | null {
  const publicId = process.env.REACT_APP_TOLT_PUBLIC_ID?.trim() || '';
  return TOLT_PUBLIC_ID_PATTERN.test(publicId) ? publicId : null;
}

function isToltReady(): boolean {
  return typeof window.tolt?.signup === 'function';
}

function waitForToltReady(script: HTMLScriptElement): Promise<boolean> {
  return new Promise((resolve) => {
    if (isToltReady()) {
      resolve(true);
      return;
    }

    let settled = false;
    const finish = (ready: boolean) => {
      if (settled) return;
      settled = true;
      if (cancelToltReadyWait === cancelWait) cancelToltReadyWait = null;
      document.removeEventListener(TOLT_LOADED_EVENT, handleReadySignal);
      document.removeEventListener(TOLT_REFERRAL_READY_EVENT, handleReadySignal);
      window.removeEventListener(STORAGE_CONSENT_EVENT, handleConsentUpdate);
      script.removeEventListener('load', handleScriptLoad);
      script.removeEventListener('error', handleError);
      window.clearInterval(pollId);
      window.clearTimeout(timeoutId);
      resolve(ready);
    };
    const cancelWait = () => finish(false);
    const handleReadySignal = () => {
      if (isToltReady()) finish(true);
    };
    const handleScriptLoad = () => {
      // The canonical script may finish before its secondary referral runtime.
      // Resolve immediately when signup is ready; otherwise keep listening for
      // either documented ready event and polling until the bounded timeout.
      handleReadySignal();
    };
    const handleError = () => finish(false);
    const handleConsentUpdate = (event: Event) => {
      const detail = (event as CustomEvent<StorageConsentEventDetail>).detail;
      if (detail?.consent.marketing !== true) finish(false);
    };
    const pollId = window.setInterval(handleReadySignal, TOLT_READY_POLL_MS);
    const timeoutId = window.setTimeout(() => finish(isToltReady()), TOLT_READY_TIMEOUT_MS);
    cancelToltReadyWait = cancelWait;

    document.addEventListener(TOLT_LOADED_EVENT, handleReadySignal);
    document.addEventListener(TOLT_REFERRAL_READY_EVENT, handleReadySignal);
    window.addEventListener(STORAGE_CONSENT_EVENT, handleConsentUpdate);
    script.addEventListener('load', handleScriptLoad, { once: true });
    script.addEventListener('error', handleError, { once: true });
  });
}

export function getToltReferralCookie(): string | null {
  if (!hasAffiliateMarketingConsent()) return null;
  const referral = readCookieValue('tolt_referral');
  if (isValidAffiliateReferral(referral)) return referral;

  // The cookie is canonical. The global is accepted only after this same
  // consent-gated script has initialized, covering browsers that delay making
  // a just-written cookie visible to the next task.
  return isToltReady() && isValidAffiliateReferral(window.tolt_referral)
    ? window.tolt_referral
    : null;
}

export function getAffiliateBootstrapRequestConfig(): {
  headers: Record<typeof AFFILIATE_HEADER_NAME, string>;
} | undefined {
  const referral = getToltReferralCookie();
  if (!referral) return undefined;
  return { headers: { [AFFILIATE_HEADER_NAME]: referral } };
}

function getCurrentAffiliateReferralQuery(): string | null {
  if (!isBrowser()) return null;

  const values = new URLSearchParams(window.location.search).getAll('ref');
  return values.length === 1 && isValidAffiliateReferral(values[0])
    ? values[0]
    : null;
}

function waitForAffiliateReferral(): Promise<void> {
  return new Promise((resolve) => {
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      document.removeEventListener(TOLT_LOADED_EVENT, handleReadySignal);
      document.removeEventListener(TOLT_REFERRAL_READY_EVENT, handleReadySignal);
      window.removeEventListener(STORAGE_CONSENT_EVENT, handleConsentUpdate);
      window.clearInterval(pollId);
      window.clearTimeout(timeoutId);
      resolve();
    };
    const handleReadySignal = () => {
      if (getAffiliateBootstrapRequestConfig()) finish();
    };
    const handleConsentUpdate = (event: Event) => {
      const detail = (event as CustomEvent<StorageConsentEventDetail>).detail;
      if (detail?.consent.marketing !== true) finish();
    };
    const pollId = window.setInterval(handleReadySignal, TOLT_READY_POLL_MS);
    const timeoutId = window.setTimeout(finish, AFFILIATE_BOOTSTRAP_PREFLIGHT_TIMEOUT_MS);

    document.addEventListener(TOLT_LOADED_EVENT, handleReadySignal);
    document.addEventListener(TOLT_REFERRAL_READY_EVENT, handleReadySignal);
    window.addEventListener(STORAGE_CONSENT_EVENT, handleConsentUpdate);
    handleReadySignal();
  });
}

export async function prepareAffiliateBootstrapRequestConfig(): Promise<{
  headers: Record<typeof AFFILIATE_HEADER_NAME, string>;
} | undefined> {
  const immediateConfig = getAffiliateBootstrapRequestConfig();
  if (immediateConfig) return immediateConfig;

  const referralQuery = getCurrentAffiliateReferralQuery();
  if (
    !referralQuery
    || !hasAffiliateMarketingConsent()
    || !getConfiguredPublicId()
  ) {
    return undefined;
  }

  if (
    !affiliateBootstrapPreflightPromise
    || affiliateBootstrapPreflightKey !== referralQuery
  ) {
    affiliateBootstrapPreflightKey = referralQuery;
    affiliateBootstrapPreflightPromise = waitForAffiliateReferral();
    void ensureToltLoaded().catch(() => false);
  }

  await affiliateBootstrapPreflightPromise;
  return getAffiliateBootstrapRequestConfig();
}

export async function ensureToltLoaded(): Promise<boolean> {
  if (!isBrowser() || !hasAffiliateMarketingConsent()) return false;
  if (isToltReady()) return true;

  const publicId = getConfiguredPublicId();
  if (!publicId) return false;
  if (toltReadyPromise) return toltReadyPromise;

  let script = document.getElementById(TOLT_SCRIPT_ID) as HTMLScriptElement | null;
  const shouldAppend = !script;
  if (!script) {
    script = document.createElement('script');
    script.id = TOLT_SCRIPT_ID;
    script.async = true;
    script.src = TOLT_PRIMARY_SCRIPT_URL;
    script.setAttribute('data-tolt', publicId);
  }

  markToltRuntimeStarted();

  const trackedReadyPromise = waitForToltReady(script).then((ready) => {
    if (!ready && toltReadyPromise === trackedReadyPromise) {
      // Keep the one script element, but allow a later account/profile load to
      // wait again in case the vendor's secondary dependency recovered late.
      toltReadyPromise = null;
    }
    return ready;
  });
  toltReadyPromise = trackedReadyPromise;

  if (shouldAppend) {
    document.head.appendChild(script);
  }
  return toltReadyPromise;
}

export function revokeToltClient(
  reloadIfStarted = false,
  reloadPage?: () => void,
): void {
  if (!isBrowser()) return;

  cancelToltReadyWait?.();
  revokeToltRuntime({
    primaryScriptIds: [TOLT_SCRIPT_ID],
    reloadIfStarted,
    reloadPage,
  });
  toltReadyPromise = null;
  affiliateBootstrapPreflightKey = null;
  affiliateBootstrapPreflightPromise = null;
}

function isDuplicateSignupResult(result: ToltSignupResult | null | undefined): boolean {
  return typeof result?.message === 'string' && /already exists|already signed up|duplicate/i.test(result.message);
}

function isDuplicateSignupError(error: unknown): boolean {
  if (typeof error === 'string') {
    return /already exists|already signed up|duplicate/i.test(error);
  }
  if (!error || typeof error !== 'object') return false;
  const candidate = error as {
    status?: number;
    response?: { status?: number; data?: { message?: string } };
    message?: string;
  };
  return candidate.status === 409
    || candidate.response?.status === 409
    || /already exists|already signed up|duplicate/i.test(
      candidate.response?.data?.message || candidate.message || '',
    );
}

async function submitToltLead(identifier: string): Promise<boolean> {
  if (completedLeadIdentifiers.has(identifier)) return true;
  if (!hasAffiliateMarketingConsent()) return false;
  if (!await ensureToltLoaded()) return false;
  if (!hasAffiliateMarketingConsent()) return false;

  const referral = getToltReferralCookie();
  if (!referral || !window.tolt_data) return false;
  if (window.tolt_data.customer_id) {
    completedLeadIdentifiers.add(identifier);
    return true;
  }

  const signup = window.tolt?.signup;
  if (typeof signup !== 'function') return false;

  try {
    const result = await signup(identifier);
    if (!hasAffiliateMarketingConsent()) {
      revokeToltClient(false);
      return false;
    }
    if (result?.customer_id || isDuplicateSignupResult(result)) {
      completedLeadIdentifiers.add(identifier);
      return true;
    }
    return false;
  } catch (error) {
    if (!hasAffiliateMarketingConsent()) {
      revokeToltClient(false);
      return false;
    }
    if (isDuplicateSignupError(error)) {
      completedLeadIdentifiers.add(identifier);
      return true;
    }
    return false;
  }
}

export function registerVerifiedAffiliateLead(identifier: string | null | undefined): Promise<boolean> {
  const normalizedIdentifier = typeof identifier === 'string' ? identifier : '';
  // The backend owns this opaque value. Refuse email-shaped, whitespace-bearing,
  // or otherwise non-opaque values so account PII can never reach Tolt here.
  if (!OPAQUE_LEAD_IDENTIFIER_PATTERN.test(normalizedIdentifier)) {
    return Promise.resolve(false);
  }
  if (completedLeadIdentifiers.has(normalizedIdentifier)) {
    return Promise.resolve(true);
  }

  const existing = inFlightLeadSubmissions.get(normalizedIdentifier);
  if (existing) return existing;

  const submission = submitToltLead(normalizedIdentifier).finally(() => {
    inFlightLeadSubmissions.delete(normalizedIdentifier);
  });
  inFlightLeadSubmissions.set(normalizedIdentifier, submission);
  return submission;
}
